import type { RtcMetricName } from './rtc-stats'

type Sample = Partial<Record<RtcMetricName, number>>
type Counter = { timestamp: number; bytes?: number; frames?: number; received?: number; packets?: number; lost?: number }

/** Reads native counters asynchronously. A slow getStats never overlaps the next poll. */
export class RtcSampler {
  private timer?: ReturnType<typeof setTimeout>
  private stopped = false
  private readonly previous = new Map<string, Counter>()

  constructor(
    private readonly connection: RTCPeerConnection,
    private readonly enabled: () => boolean,
    private readonly record: (sample: Sample) => void,
  ) { this.schedule() }

  stop(): void { this.stopped = true; clearTimeout(this.timer); this.previous.clear() }

  private schedule(): void {
    if (!this.stopped) this.timer = setTimeout(() => { void this.poll() }, 250)
  }

  private async poll(): Promise<void> {
    const started = performance.now()
    try {
      if (this.stopped || !this.enabled() || this.connection.connectionState === 'closed') {
        this.previous.clear()
        return
      }
      const report = await this.connection.getStats()
      if (this.stopped || !this.enabled()) return
      const sample: Sample = {}
      const seen = new Set<string>()
      report.forEach(entry => {
        const s = entry as RTCStats & Record<string, any>
        if (s.type === 'candidate-pair' && s.state === 'succeeded' && s.nominated && typeof s.currentRoundTripTime === 'number') {
          sample.down_rtt_ms = s.currentRoundTripTime * 1000
          return
        }
        if ((s.kind ?? s.mediaType) !== 'video') return
        if (s.type === 'remote-inbound-rtp') {
          if (typeof s.roundTripTime === 'number') sample.up_rtt_ms = s.roundTripTime * 1000
          if (typeof s.fractionLost === 'number') sample.up_loss_rate = s.fractionLost
          return
        }
        if (s.type !== 'outbound-rtp' && s.type !== 'inbound-rtp') return
        seen.add(s.id)
        const up = s.type === 'outbound-rtp'
        const current: Counter = {
          timestamp: s.timestamp, bytes: up ? s.bytesSent : s.bytesReceived,
          frames: up ? s.framesSent : s.framesDecoded, received: s.framesReceived,
          packets: s.packetsReceived, lost: s.packetsLost,
        }
        const prev = this.previous.get(s.id)
        this.previous.set(s.id, current)
        if (!up && typeof s.jitter === 'number') sample.jitter_ms = Math.max(sample.jitter_ms ?? 0, s.jitter * 1000)
        if (!prev || current.timestamp <= prev.timestamp) return
        const seconds = (current.timestamp - prev.timestamp) / 1000
        const delta = (key: keyof Counter): number | undefined => {
          const a = current[key], b = prev[key]
          return typeof a === 'number' && typeof b === 'number' && a >= b ? a - b : undefined
        }
        const bytes = delta('bytes'), frames = delta('frames'), received = delta('received')
        const bitrateMetric = up ? 'up_bitrate_kbps' : 'down_bitrate_kbps'
        const fpsMetric = up ? 'up_fps' : 'down_decode_fps'
        if (bytes !== undefined) sample[bitrateMetric] = (sample[bitrateMetric] ?? 0) + bytes * 8 / seconds / 1000
        if (frames !== undefined) sample[fpsMetric] = Math.max(sample[fpsMetric] ?? 0, frames / seconds)
        if (!up && received !== undefined) sample.down_receive_fps = Math.max(sample.down_receive_fps ?? 0, received / seconds)
        if (!up) {
          const packets = delta('packets'), lost = delta('lost')
          if (packets !== undefined && lost !== undefined && packets + lost > 0) sample.down_loss_rate = lost / (packets + lost)
        }
      })
      this.previous.forEach((_, id) => { if (!seen.has(id)) this.previous.delete(id) })
      if (Object.keys(sample).length) this.record(sample)
    } catch {
      // Collection must never reject into the RTC lifecycle.
    } finally {
      if (!this.stopped) this.timer = setTimeout(() => { void this.poll() }, Math.max(250 - (performance.now() - started), 0))
    }
  }
}
